import { FC } from "react";
import { Box, Flex, Grid, Tabs } from "@radix-ui/themes";
import { Events } from "@/models/database/event/Events.model";
import DateObject from "react-date-object";
import { OverviewArchive } from "@/components";
import { SingleEventNews } from "./SingleEventNews";
import { SingleEventChallenges } from "./SingleEventChallenges";
import { SingleEventMeta } from "./SingleEventMeta";
import { SingleEventSchedule } from "./SingleEventSchedule";
import { Zones } from "@/models/database/zones/Zones.model";

interface SingleEventProps {
  data: Events;
  zone?: Zones;
}

export const SingleEventUI: FC<SingleEventProps> = ({
  data,
  zone,
}): JSX.Element => {
  const startDate = new DateObject(data.startDate.toDate());
  const endDate = new DateObject(data.endDate.toDate());

  const headline = zone ? `${zone.title} @ ${data.title}` : data.title;
  const relatedNewsSlugs = data.news;
  const relatedChallengesSlugs = data.challenges;
  const relatedSchedule = data.schedule;

  const hasNews = relatedNewsSlugs.length > 0;
  const hasChallenges = relatedChallengesSlugs.length > 0;
  const hasSchedule = relatedSchedule.length > 0;

  return (
    <>
      <OverviewArchive
        headline={headline}
        subtitle={data.desc}
        disableBadges={true}
        background={data.coverURL}
      />
      <Tabs.Root
        defaultValue={
          hasSchedule ? "schedule" : hasChallenges ? "challenges" : "news"
        }
      >
        <Grid columns="4" gapX="9">
          <Box gridColumn={{ initial: "1 / -1", lg: "1 / 4" }}>
            <Tabs.List>
              {hasSchedule && (
                <Tabs.Trigger value="schedule">Schedule</Tabs.Trigger>
              )}
              {hasChallenges && (
                <Tabs.Trigger value="challenges">Challenges</Tabs.Trigger>
              )}
              {hasNews && <Tabs.Trigger value="news">News</Tabs.Trigger>}
            </Tabs.List>
          </Box>
        </Grid>
        <Grid columns="4" gapX="9">
          <Box gridColumn={{ initial: "1 / -1", lg: "1 / 4" }}>
            <Box pt="3">
              <Tabs.Content value="schedule">
                {/* Schedule */}
                <SingleEventSchedule schedule={relatedSchedule} />
              </Tabs.Content>

              <Tabs.Content value="challenges">
                {/* Challenges */}
                <SingleEventChallenges
                  challenges={relatedChallengesSlugs}
                  zone={zone}
                  endDate={endDate}
                  startDate={startDate}
                />
              </Tabs.Content>

              <Tabs.Content value="news">
                {/* News */}
                <SingleEventNews news={relatedNewsSlugs} />
              </Tabs.Content>
            </Box>

            <Flex direction="column" gap="7"></Flex>
          </Box>
          <Box gridColumn={{ initial: "1 / -1", lg: "4 / 5" }}>
            <SingleEventMeta
              startDate={startDate}
              endDate={endDate}
              location={data.location}
            />
          </Box>
        </Grid>
      </Tabs.Root>
    </>
  );
};
